import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Camera, Upload, Calendar, TrendingUp, Trash2, Eye, Scale, Ruler, ChevronLeft, ChevronRight, X, Plus, Sparkles, MessageCircle, Trophy, Target, Flame, BarChart3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProgressPhotos = ({ token }) => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState([]);
  const [timeline, setTimeline] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [comparePhotos, setComparePhotos] = useState({ photo1: null, photo2: null });
  const [activeTab, setActiveTab] = useState('gallery');
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const system = user?.measurement_system || user?.profile?.measurement_system || 'metric';
  const unit = system === 'imperial' ? 'lbs' : 'kg';
  
  // AI Analysis state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [quickQuestion, setQuickQuestion] = useState('');
  const [quickResponse, setQuickResponse] = useState('');
  const [askingQuestion, setAskingQuestion] = useState(false);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    photoType: 'front',
    notes: '',
    measurements: {
      weight: '',
      chest: '',
      waist: '',
      hips: '',
      arms: '',
      thighs: ''
    }
  });

  useEffect(() => {
    fetchPhotos();
    fetchStats();
  }, [token]);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/progress/photos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos(response.data.photos || []);
      
      // Also fetch timeline
      const timelineRes = await axios.get(`${API}/progress/timeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeline(timelineRes.data.timeline || {});
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/progress/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedPhoto({
          preview: reader.result,
          data: reader.result
        });
        setShowUploadModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedPhoto) return;
    
    setUploading(true);
    try {
      // Convert measurement strings to numbers, filter out empty
      const measurements = {};
      Object.entries(uploadForm.measurements).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          measurements[key] = parseFloat(value);
        }
      });
      
      const response = await axios.post(`${API}/progress/photos`, {
        image_data: selectedPhoto.data,
        photo_type: uploadForm.photoType,
        notes: uploadForm.notes,
        measurements: Object.keys(measurements).length > 0 ? measurements : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reset form and refresh
      setShowUploadModal(false);
      setSelectedPhoto(null);
      setUploadForm({
        photoType: 'front',
        notes: '',
        measurements: { weight: '', chest: '', waist: '', hips: '', arms: '', thighs: '' }
      });
      fetchPhotos();
      fetchStats();
      
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      await axios.delete(`${API}/progress/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPhotos();
      fetchStats();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  // AI Analysis Functions
  const runAIAnalysis = async () => {
    if (photos.length < 2) {
      alert('Please upload at least 2 progress photos for AI analysis.');
      return;
    }
    
    setAnalyzing(true);
    setAiAnalysis(null);
    
    try {
      const response = await axios.post(`${API}/progress/ai-analysis`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAiAnalysis(response.data);
        setActiveTab('ai-analysis');
      } else {
        alert(response.data.message || 'Analysis failed. Please try again.');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      alert('Failed to run AI analysis. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const askQuickQuestion = async () => {
    if (!quickQuestion.trim()) return;
    
    setAskingQuestion(true);
    setQuickResponse('');
    
    try {
      const response = await axios.post(`${API}/progress/quick-feedback`, {
        question: quickQuestion
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setQuickResponse(response.data.response);
    } catch (error) {
      console.error('Quick question error:', error);
      setQuickResponse('Sorry, I could not process your question. Please try again.');
    } finally {
      setAskingQuestion(false);
    }
  };

  // Parse markdown-style analysis response
  const parseAnalysisSection = (text, sectionTitle) => {
    const regex = new RegExp(`## ${sectionTitle}[\\s\\S]*?(?=## |$)`, 'i');
    const match = text.match(regex);
    if (match) {
      return match[0].replace(`## ${sectionTitle}`, '').trim();
    }
    return null;
  };

  const handleCompare = async () => {
    if (!comparePhotos.photo1 || !comparePhotos.photo2) return;
    
    try {
      const response = await axios.get(
        `${API}/progress/compare?photo1_id=${comparePhotos.photo1.id}&photo2_id=${comparePhotos.photo2.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComparePhotos({
        ...comparePhotos,
        comparison: response.data
      });
    } catch (error) {
      console.error('Compare error:', error);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getMonthName = (monthKey) => {
    const [year, month] = monthKey.split('-');
    return new Date(year, parseInt(month) - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="progress-photos-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{t('progressPhotos.title')}</h2>
          <p className="text-gray-600 dark:text-gray-300">{t('progressPhotos.subtitle')}</p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
          data-testid="upload-photo-btn"
        >
          <Camera size={20} />
          {t('progressPhotos.addPhoto')}
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*"
          className="hidden"
        />
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Camera className="text-orange-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{stats.total_photos}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('progressPhotos.totalPhotos')}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{stats.consistency_score}%</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('progressPhotos.consistency')}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                  {stats.first_photo_date ? Math.ceil((new Date() - new Date(stats.first_photo_date)) / (1000 * 60 * 60 * 24)) : 0}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('progressPhotos.daysTracking')}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Scale className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                  {Object.keys(stats.measurement_trends || {}).length}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('progressPhotos.trackedMetrics')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        {['gallery', 'timeline', 'compare', 'ai-analysis'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === tab
                ? 'text-orange-600 border-b-2 border-orange-500'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300'
            }`}
            data-testid={`tab-${tab}`}
          >
            {tab === 'ai-analysis' && <Sparkles size={16} />}
            {tab === 'gallery' ? t('progressPhotos.gallery') : tab === 'timeline' ? t('progressPhotos.timeline') : tab === 'compare' ? t('progressPhotos.compare') : t('progressPhotos.aiAnalysis')}
          </button>
        ))}
      </div>

      {/* Gallery View */}
      {activeTab === 'gallery' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.length === 0 ? (
            <div className="col-span-full text-center py-16 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-xl">
              <Camera size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-300 mb-2">{t('progressPhotos.noPhotos')}</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">{t('progressPhotos.noPhotosDesc')}</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg"
              >
                {t('progressPhotos.uploadFirst')}
              </button>
            </div>
          ) : (
            photos.map((photo) => (
              <div
                key={photo.id}
                className="relative group bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all"
                data-testid={`photo-${photo.id}`}
              >
                <img
                  src={`data:image/jpeg;base64,${photo.image_data}`}
                  alt={`Progress - ${photo.photo_type}`}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 flex gap-2">
                    <button
                      onClick={() => setSelectedPhoto({ ...photo, preview: `data:image/jpeg;base64,${photo.image_data}` })}
                      className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100"
                    >
                      <Eye size={20} className="text-gray-700 dark:text-gray-300" />
                    </button>
                    <button
                      onClick={() => handleDelete(photo.id)}
                      className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-red-100"
                    >
                      <Trash2 size={20} className="text-red-500" />
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded capitalize">
                      {photo.photo_type}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(photo.date)}</span>
                  </div>
                  {photo.measurements && Object.keys(photo.measurements).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.entries(photo.measurements).slice(0, 2).map(([key, value]) => (
                        <span key={key} className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                          {key}: {value}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Timeline View */}
      {activeTab === 'timeline' && (
        <div className="space-y-8">
          {Object.keys(timeline).length === 0 ? (
            <div className="text-center py-16 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-xl">
              <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">{t('progressPhotos.noTimeline')}</p>
            </div>
          ) : (
            Object.entries(timeline).map(([monthKey, monthPhotos]) => (
              <div key={monthKey}>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Calendar size={20} className="text-orange-500" />
                  {getMonthName(monthKey)}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({monthPhotos.length} photos)</span>
                </h3>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {monthPhotos.map((photo) => (
                    <div
                      key={photo.id}
                      className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition-all"
                      onClick={() => {
                        const fullPhoto = photos.find(p => p.id === photo.id);
                        if (fullPhoto) setSelectedPhoto({ ...fullPhoto, preview: `data:image/jpeg;base64,${fullPhoto.image_data}` });
                      }}
                    >
                      <div className="h-24 bg-gray-200 flex items-center justify-center">
                        <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{photo.photo_type}</span>
                      </div>
                      <div className="p-2 text-center">
                        <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(photo.date)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Compare View */}
      {activeTab === 'compare' && (
        <div className="space-y-6">
          <p className="text-gray-600 dark:text-gray-300">{t('progressPhotos.selectTwoPhotos')}</p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Before Photo */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300">{t('progressPhotos.before')}</h4>
              {comparePhotos.photo1 ? (
                <div className="relative">
                  <img
                    src={`data:image/jpeg;base64,${comparePhotos.photo1.image_data}`}
                    alt="Before"
                    className="w-full h-64 object-cover rounded-xl"
                  />
                  <button
                    onClick={() => setComparePhotos({ ...comparePhotos, photo1: null })}
                    className="absolute top-2 right-2 p-1 bg-white dark:bg-gray-800 rounded-full shadow"
                  >
                    <X size={16} />
                  </button>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{formatDate(comparePhotos.photo1.date)}</p>
                </div>
              ) : (
                <div
                  className="h-64 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center cursor-pointer hover:border-orange-400 transition-colors"
                  onClick={() => setShowCompareModal('photo1')}
                >
                  <div className="text-center">
                    <Plus size={32} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">{t('progressPhotos.selectBeforePhoto')}</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* After Photo */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300">{t('progressPhotos.after')}</h4>
              {comparePhotos.photo2 ? (
                <div className="relative">
                  <img
                    src={`data:image/jpeg;base64,${comparePhotos.photo2.image_data}`}
                    alt="After"
                    className="w-full h-64 object-cover rounded-xl"
                  />
                  <button
                    onClick={() => setComparePhotos({ ...comparePhotos, photo2: null })}
                    className="absolute top-2 right-2 p-1 bg-white dark:bg-gray-800 rounded-full shadow"
                  >
                    <X size={16} />
                  </button>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{formatDate(comparePhotos.photo2.date)}</p>
                </div>
              ) : (
                <div
                  className="h-64 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center cursor-pointer hover:border-orange-400 transition-colors"
                  onClick={() => setShowCompareModal('photo2')}
                >
                  <div className="text-center">
                    <Plus size={32} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">{t('progressPhotos.selectAfterPhoto')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Comparison Results */}
          {comparePhotos.comparison && comparePhotos.comparison.measurement_changes && Object.keys(comparePhotos.comparison.measurement_changes).length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
              <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-4">{t('progressPhotos.measurementChanges')}</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(comparePhotos.comparison.measurement_changes).map(([key, data]) => (
                  <div key={key} className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg p-3">
                    <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{key}</p>
                    <p className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                      {data.before} → {data.after}
                    </p>
                    <p className={`text-sm font-medium ${data.change < 0 ? 'text-green-600' : data.change > 0 ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
                      {data.change > 0 ? '+' : ''}{data.change} ({data.change_percent}%)
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {comparePhotos.photo1 && comparePhotos.photo2 && (
            <button
              onClick={handleCompare}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-semibold transition-colors"
            >
              {t('progressPhotos.comparePhotos')}
            </button>
          )}
        </div>
      )}

      {/* AI Analysis View */}
      {activeTab === 'ai-analysis' && (
        <div className="space-y-6">
          {/* Run Analysis Button */}
          <div className="bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl p-6 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Sparkles size={24} />
                  AI Progress Analysis
                </h3>
                <p className="text-orange-100 mt-1">
                  {t('progressPhotos.aiProgressDesc')}
                </p>
              </div>
              <button
                onClick={runAIAnalysis}
                disabled={analyzing || photos.length < 2}
                className="bg-white dark:bg-gray-800 text-orange-600 hover:bg-orange-50 disabled:bg-gray-200 disabled:text-gray-400 px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 shadow-lg"
                data-testid="run-ai-analysis-btn"
              >
                {analyzing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600"></div>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} />
                    {t('progressPhotos.analyzeMyProgress')}
                  </>
                )}
              </button>
            </div>
            {photos.length < 2 && (
              <p className="text-orange-200 text-sm mt-3">
                {t('progressPhotos.needMorePhotos')}
              </p>
            )}
          </div>

          {/* Analysis Results */}
          {aiAnalysis && aiAnalysis.success && (
            <div className="space-y-4">
              {/* Photos Compared */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100">
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Camera size={18} />
                  Photos Analyzed
                </h4>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('progressPhotos.firstPhoto')}</p>
                    <p className="font-medium text-gray-700 dark:text-gray-300">{formatDate(aiAnalysis.photos_compared.oldest.date)}</p>
                  </div>
                  <div className="flex-1 h-0.5 bg-gradient-to-r from-gray-300 via-orange-400 to-green-400 rounded-full"></div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('progressPhotos.latestPhoto')}</p>
                    <p className="font-medium text-gray-700 dark:text-gray-300">{formatDate(aiAnalysis.photos_compared.newest.date)}</p>
                  </div>
                </div>
                <p className="text-center text-sm text-orange-600 font-medium mt-2">
                  {aiAnalysis.journey_duration_days} days of progress ({Math.floor(aiAnalysis.journey_duration_days / 7)} weeks)
                </p>
              </div>

              {/* Measurement Changes */}
              {aiAnalysis.measurement_changes && Object.keys(aiAnalysis.measurement_changes).length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100">
                  <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                    <BarChart3 size={18} />
                    Measurement Progress
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(aiAnalysis.measurement_changes).map(([key, data]) => (
                      <div key={key} className={`rounded-lg p-3 ${
                        data.change < 0 ? 'bg-green-50 border border-green-200' : 
                        data.change > 0 ? 'bg-orange-50 border border-orange-200' : 
                        'bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 border border-gray-200 dark:border-gray-700'
                      }`}>
                        <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{key}</p>
                        <p className="text-lg font-bold text-gray-800 dark:text-gray-100">
                          {data.start} → {data.current}
                        </p>
                        <p className={`text-sm font-semibold ${
                          data.change < 0 ? 'text-green-600' : 
                          data.change > 0 ? 'text-orange-600' : 
                          'text-gray-500 dark:text-gray-400'
                        }`}>
                          {data.change > 0 ? '+' : ''}{data.change} ({data.percent_change}%)
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Analysis Content */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500 to-pink-500 p-4">
                  <h4 className="font-bold text-white flex items-center gap-2">
                    <Sparkles size={20} />
                    Your Personalized Analysis
                  </h4>
                </div>
                <div className="p-6 prose prose-orange max-w-none">
                  {aiAnalysis.analysis.split('## ').filter(s => s).map((section, idx) => {
                    const lines = section.split('\n');
                    const title = lines[0];
                    const content = lines.slice(1).join('\n').trim();
                    
                    // Get icon for section
                    let Icon = Target;
                    if (title.includes('Progress')) Icon = TrendingUp;
                    if (title.includes('Achievement') || title.includes('Milestone')) Icon = Trophy;
                    if (title.includes('Recommendation')) Icon = Target;
                    if (title.includes('Motivation')) Icon = Flame;
                    if (title.includes('Goal')) Icon = Target;
                    
                    return (
                      <div key={idx} className="mb-6 last:mb-0">
                        <h5 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2 mb-3">
                          <Icon size={20} className="text-orange-500" />
                          {title.replace(/[🎯🏆💡🔥📊]/g, '').trim()}
                        </h5>
                        <div className="text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed">
                          {content}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Quick Question Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100">
            <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
              <MessageCircle size={18} />
              Ask About Your Progress
            </h4>
            <div className="flex gap-3">
              <input
                type="text"
                value={quickQuestion}
                onChange={(e) => setQuickQuestion(e.target.value)}
                placeholder={t('progressPhotos.askPlaceholder')}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                onKeyPress={(e) => e.key === 'Enter' && askQuickQuestion()}
              />
              <button
                onClick={askQuickQuestion}
                disabled={askingQuestion || !quickQuestion.trim()}
                className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
              >
                {askingQuestion ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Ask'
                )}
              </button>
            </div>
            
            {/* Quick Response */}
            {quickResponse && (
              <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{quickResponse}</p>
              </div>
            )}
            
            {/* Suggested Questions */}
            <div className="mt-4">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{t('progressPhotos.suggestedQuestions')}</p>
              <div className="flex flex-wrap gap-2">
                {[
                  t('progressPhotos.q1'),
                  t('progressPhotos.q2'),
                  t('progressPhotos.q3'),
                  t('progressPhotos.q4')
                ].map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setQuickQuestion(q);
                    }}
                    className="text-sm text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-1 rounded-full transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && selectedPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">{t('progressPhotos.uploadProgressPhoto')}</h3>
                <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-gray-100 dark:bg-gray-700 rounded-full">
                  <X size={20} />
                </button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Preview */}
                <div>
                  <img
                    src={selectedPhoto.preview}
                    alt="Preview"
                    className="w-full h-64 object-cover rounded-xl"
                  />
                </div>
                
                {/* Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('progressPhotos.photoType')}</label>
                    <select
                      value={uploadForm.photoType}
                      onChange={(e) => setUploadForm({ ...uploadForm, photoType: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="front">{t('progressPhotos.frontView')}</option>
                      <option value="side">{t('progressPhotos.sideView')}</option>
                      <option value="back">{t('progressPhotos.backView')}</option>
                      <option value="flexing">{t('progressPhotos.flexing')}</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('progressPhotos.notesOptional')}</label>
                    <textarea
                      value={uploadForm.notes}
                      onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                      placeholder={t('progressPhotos.notesPlaceholder')}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
              
              {/* Measurements */}
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Ruler size={16} />
                  {t('progressPhotos.addMeasurements')}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(uploadForm.measurements).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1 capitalize">{key}</label>
                      <input
                        type="number"
                        step="0.1"
                        value={value}
                        onChange={(e) => setUploadForm({
                          ...uploadForm,
                          measurements: { ...uploadForm.measurements, [key]: e.target.value }
                        })}
                        placeholder={key === 'weight' ? unit : 'cm'}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="w-full mt-6 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {t('progressPhotos.uploading')}
                  </>
                ) : (
                  <>
                    <Upload size={20} />
                    {t('progressPhotos.saveProgressPhoto')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Selector Modal for Compare */}
      {showCompareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">{t('progressPhotos.selectPhoto')}</h3>
                <button onClick={() => setShowCompareModal(false)} className="p-2 hover:bg-gray-100 dark:bg-gray-700 rounded-full">
                  <X size={20} />
                </button>
              </div>
              
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {photos.map((photo) => (
                  <div
                    key={photo.id}
                    className="cursor-pointer rounded-lg overflow-hidden border-2 border-transparent hover:border-orange-500 transition-colors"
                    onClick={() => {
                      if (showCompareModal === 'photo1') {
                        setComparePhotos({ ...comparePhotos, photo1: photo });
                      } else {
                        setComparePhotos({ ...comparePhotos, photo2: photo });
                      }
                      setShowCompareModal(false);
                    }}
                  >
                    <img
                      src={`data:image/jpeg;base64,${photo.image_data}`}
                      alt={photo.photo_type}
                      className="w-full h-24 object-cover"
                    />
                    <p className="text-xs text-center py-1 bg-gray-50 dark:bg-gray-900">{formatDate(photo.date)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo View Modal */}
      {selectedPhoto && !showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl w-full">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300"
            >
              <X size={32} />
            </button>
            <img
              src={selectedPhoto.preview}
              alt="Progress"
              className="w-full max-h-[80vh] object-contain rounded-xl"
            />
            {selectedPhoto.notes && (
              <div className="mt-4 text-white text-center">
                <p>{selectedPhoto.notes}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressPhotos;
